import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as QRIcon } from '../../assets/icons/action-qr.svg';
import { ReactComponent as ResetIcon } from '../../assets/icons/action-reset.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as CCS2Icon } from '../../assets/icons/device-management/ccs2.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
// import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import { useAppBranding, currentBranding } from '../../config/branding';
import { connectorTypes, desiredPowerOutputUnits, maxPowerOutputUnits } from '../../config/enum';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import {
  AvailableDevice,
  RequestDeviceInterFace,
} from '../../interface/device-management.interface';
import DeviceQrCode from '../../pages/device-management/components/DeviceQrCode';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchAllStationsList } from '../../redux/slice/ApprovedStationManagementSlice';
import {
  fetchAllDevices,
  fetchChargePointModels,
  fetchChargePointVendors,
} from '../../redux/slice/DeviceManagementSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { RootState } from '../../redux/store';
import deviceApi from '../../utils/api/device';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';
import CustomInput from '../custom-input/CustomInput';
import CustomInputSelectBox from '../custom-input-select-box/CustomInputSelectBox';
import CustomInputSwitch from '../custom-input-switch/CustomInputSwitch';
import DeleteWarning from '../delete-warning/DeleteWarning';
import DisableSwitchWarning from '../disable-warning/DisableWarning';
import ModalBox from '../modal-box/ModalBox';
import MultiSelectDropdown from '../multi-select-field/MultiSelectField';
import SelectField from '../select-field/SelectField';
import SuccessNotification from '../success-notification/SuccessNotification';

import StationSidePanel from './StationSidePanel';

interface DeviceSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  currentData?: AvailableDevice;
  onEditModeChange?: (isEdit: boolean) => void;
}

interface Connector {
  connectorId: number;
  isReservationEnabled?: boolean;
  status?: string;
  _id?: string;
  isAvailable?: boolean;
  connectorType: string[];
}

interface ChargingPointProperties {
  _id?: string;
  chargingStation?: string;
  chargingPointName?: string;
  chargingPointSn?: string;
  maximumOutputPower?: string;
  maximumOutputPowerUnit?: string;
  isAvailable?: boolean;
  isDeviceOffline?: boolean;
  connectors: Connector[];
  chargePointModel: string;
  chargePointVendor: string;
  chargingStationName?: string;
  latitude?: string;
  longitude?: string;
  address?: string;
  accountCustomId?: string;
  accountName?: string;
  desiredPowerOutput?: number;
  powerUnit?: string;
  isJuiceBoxPncEnabled?: boolean;
  useTxDefaultProfile?: boolean;
  modelSeries?: string;
  chargeRate?: {
    unit?: string;
    value?: number;
  };
}

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Device side panel
 * @returns JSX.Element
 */
export default function DeviceSidePanel(properties: DeviceSidePanelProperties) {
  const branding = useAppBranding();
  const { t } = useTranslation('tableSidepanel');
  const { fetchIdFrom, id, onEditModeChange } = properties;
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const { currentFacilityTagsList } = useAppSelector((state: RootState) => state.facilityTags);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModal: false,
  });
  const { page, limit, loading, chargePointVendors, chargePointModels } = useAppSelector(
    (states: RootState) => states.device,
  );
  const { allStations } = useAppSelector((state) => state.approvedStation);
  const { isLoaded } = useGoogleMaps();
  const dispatch = useAppDispatch();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [isEdit, setIsEdit] = useState(false);
  const mapReference = useRef<google.maps.Map | null>(null);

  const [deviceDetails, setDeviceDetails] = useState<ChargingPointProperties>({
    connectors: [],
    chargePointModel: '',
    chargePointVendor: '',
  });

  const [editData, setEditData] = useState<any>({});
  const [formStatus, setFormStatus] = useState({
    buttonLoader: false,
  });

  const [overlaySidePanel, setOverlaySidePanel] = useState<'closed' | 'station' | 'facility'>(
    'closed',
  );

  const [deviceImage, setDeviceImage] = useState<string>('');

  const deviceGeneralDetails = [
    {
      id: 1,
      label: t('stationName'),
      key: 'chargingStationName',
      /**
       * Open side panel for station view
       * @returns void
       */
      handleClick: () => setOverlaySidePanel('station'),
    },
    {
      id: 2,
      label: t('deviceId'),
      key: 'chargingPointSn',
    },
    {
      id: 3,
      label: t('accountName'),
      key: 'accountName',
      /**
       * Open side panel for station view
       * @returns
       */
      // handleClick: () => setOverlaySidePanel('facility'),
    },
    {
      id: 4,
      label: t('deviceModel'),
      key: 'chargePointModel',
    },
    {
      id: 5,
      label: t('deviceName'),
      key: 'chargingPointName',
    },
    {
      id: 6,
      label: t('deviceSerial'),
      key: 'chargingPointSn',
    },
    {
      id: 7,
      label: t('deviceVendor'),
      key: 'chargePointVendor',
    },
    {
      id: 8,
      label: t('firmwareVersion'),
      key: 'firmwareVersion',
    },
  ];

  /**
   * Function to fetch device data based on chargePointVendor and chargePointModel
   * @param deviceData
   */
  const handleFetchDeviceImage = async (deviceData: any) => {
    try {
      const imageResponse = await deviceApi.fetchDeviceImage({
        chargePointVendor: deviceData?.chargePointVendor,
        chargePointModel: deviceData?.chargePointModel,
      });

      setDeviceImage(imageResponse?.data?.medias[0]?.url || '');
    } catch (error: any) {
      console.error(`Error while fetching charging point metadata: `, error?.message);
    }
  };

  /**
   * Function to fetch station details
   * @param stationId
   */
  const handleFetchStationDetails = React.useCallback(
    async (stationId: string) => {
      setIsLoader(true);
      try {
        const response = await deviceApi.getChargingPointById(stationId);
        setDeviceDetails(response?.data);

        await handleFetchDeviceImage(response?.data);
      } catch (error: any) {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      } finally {
        setIsLoader(false);
      }
    },
    [dispatch],
  );

  /**
   * Handle the fetch data for Api call function
   * @param data - request parameters
   */
  const fetchData = (data: RequestDeviceInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchAllDevices({
          ...data,
        }),
      );
    }
  };

  /**
   * Function to reset the device
   * @param chargingPointSn
   */
  const resetDevice = React.useCallback(
    async (chargingPointSn: string) => {
      if (!chargingPointSn) return;

      try {
        await deviceApi.resetDevice({ chargingPointSn });

        setModalBoxDetail({
          title: '',
          header: false,
          content: (
            <SuccessNotification
              title={t('deviceResetSuccess')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                fetchData({ page, limit });
              }}
            />
          ),
          notificationModal: true,
        });
      } catch (error: any) {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      }
    },
    [dispatch, page, limit, t],
  );

  /**
   * Function to reset device
   */
  const handleResetAction = () => {
    if (!deviceDetails?.chargingPointSn) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: 400,
            message: t('deviceSerialMissing'),
          },
        }),
      );
      return;
    }

    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DisableSwitchWarning
          selectedId={deviceDetails.chargingPointSn}
          onCancel={() => setModalBoxStatus('close')}
          message={t('resetConfirmMessage', { device: deviceDetails?.chargingPointName })}
          onConfirm={() => {
            if (!deviceDetails?.chargingPointSn) return;
            resetDevice(deviceDetails.chargingPointSn);
          }}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Function to handle the QR action
   */
  const handleQrAction = () => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DeviceQrCode
          handleClose={() => setModalBoxStatus('close')}
          deviceSN={deviceDetails.chargingPointSn || ''}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Handle the modal close function
   */
  const handleCloseModal = () => {
    fetchData({
      page,
      limit,
    });
    setModalBoxStatus('close');
  };

  /**
   * Toggle edit mode
   */
  const toggleEditMode = () => {
    if (isEdit) {
      setIsEdit(false);
      setEditData({});
      onEditModeChange?.(false);
      return;
    }

    setEditData({
      chargingStation: deviceDetails.chargingStation || '',
      chargingPointName: deviceDetails.chargingPointName || '',
      chargingPointSn: deviceDetails.chargingPointSn || '',
      chargePointModel: deviceDetails.chargePointModel || '',
      chargePointVendor: deviceDetails.chargePointVendor || '',
      connectors: deviceDetails.connectors || [],
      maximumOutputPower: deviceDetails.maximumOutputPower || 0,
      maximumOutputPowerUnit: deviceDetails.maximumOutputPowerUnit || 'kW',
      // desiredPowerOutput: deviceDetails.desiredPowerOutput || 0,
      desiredPowerOutput: (deviceDetails?.chargeRate?.value || 0) / 1000,
      powerUnit: deviceDetails.chargeRate?.unit || 'W',
      isJuiceBoxPncEnabled: deviceDetails.isJuiceBoxPncEnabled || false,
      useTxDefaultProfile: deviceDetails.useTxDefaultProfile || false,
      modelSeries: deviceDetails?.modelSeries || '',
    });

    setIsEdit(true);
    onEditModeChange?.(true);

    // Fetch stations if not available
    if (!allStations || allStations.length === 0) {
      dispatch(fetchAllStationsList());
    }
  };

  /**
   * Handle Input Change
   */
  const handleInputChange = (name: string, value: string | number | any) => {
    setEditData((previous: any) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle individual connector type changes.
   * @param {number} connectorId - The ID of the connector to update
   * @param {string[]} selectedTypes - The new selected connector types
   */
  const handleConnectorTypeChange = (connectorId: number, selectedTypes: string[]) => {
    const updatedConnectors = editData.connectors.map((connector: Connector) =>
      connector.connectorId === connectorId
        ? { ...connector, connectorType: selectedTypes }
        : connector,
    );
    setEditData({
      ...editData,
      connectors: updatedConnectors,
    });
  };

  /**
   * Submit device details
   */
  const handleSubmitDeviceDetails = async () => {
    setFormStatus({ buttonLoader: true });

    // Construct payload - filter out fields that shouldn't be sent or validate
    const {
      chargePointVendor,
      chargePointModel,
      desiredPowerOutput,
      powerUnit,
      modelSeries, // Remove this from Edit api
      connectors,
      ...rest
    } = editData;

    const payload: any = {
      ...rest,
      connectors: connectors?.map((connector: Connector) => ({
        connectorId: connector.connectorId,
        connectorType: connector.connectorType,
      })),
      // chargeRate: {
      //   unit: powerUnit,
      //   value: desiredPowerOutput,
      // },
    };

    try {
      await deviceApi.editDevice(payload, deviceDetails._id || '');

      setFormStatus({ buttonLoader: false });
      setIsEdit(false);
      onEditModeChange?.(false);

      // Refresh details
      if (deviceDetails._id) {
        handleFetchStationDetails(deviceDetails._id);
      }

      setModalBoxStatus('open');
      setModalBoxDetail({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={t('deviceUpdatedSuccess')} // Ensure you have this key or use a generic one
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setModalBoxStatus('close');
              const newParameters = new URLSearchParams(searchParameters);
              newParameters.delete('deviceId');
              setSearchParameters(newParameters);
              fetchData({ page, limit });
            }}
          />
        ),
        notificationModal: true,
      });
    } catch (error: any) {
      setFormStatus({ buttonLoader: false });
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * Function to delete the device
   */
  const handleDeleteDevice = (deviceId: string) => {
    deviceApi
      .deleteDevice(deviceId)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title={t('deviceDeletedSuccess')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                const newParameters = new URLSearchParams(searchParameters);
                newParameters.delete('deviceId');
                setSearchParameters(newParameters);
                handleCloseModal();
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to handle the delete action
   */
  const handleDeleteAction = () => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={deviceDetails._id}
          title={t('deleteConfirmTitle')}
          description={t('deleteConfirmDescription')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(deviceId) => deviceId && handleDeleteDevice(deviceId)}
        />
      ),
      notificationModal: true,
    });
  };

  const validPosition = React.useMemo(() => {
    const lat = parseFloat(deviceDetails.latitude || '');
    const lng = parseFloat(deviceDetails.longitude || '');
    return !Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null;
  }, [deviceDetails.latitude, deviceDetails.longitude]);

  const stationId = React.useMemo(() => {
    return fetchIdFrom === 'queryParams' ? searchParameters.get('deviceId') : id;
  }, [fetchIdFrom, searchParameters, id]);

  /**
   * Function to fetch charge point models by chargePointVendor
   */
  const fetchChargePointModel = () => {
    dispatch(fetchChargePointModels(editData?.chargePointVendor));
  };

  useEffect(() => {
    if (editData?.chargePointVendor) {
      fetchChargePointModel();
    }
  }, [editData?.chargePointVendor]);

  useEffect(() => {
    if (!stationId) return;
    setOverlaySidePanel('closed');
    handleFetchStationDetails(stationId);

    // Fetch charge point vendors, if chargePointVendors list is empty
    if (!chargePointVendors.length) {
      dispatch(fetchChargePointVendors());
    }
  }, [stationId, handleFetchStationDetails]);

  if (isLoader) {
    return (
      <div className="loader-wrap">
        <div className="loader">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="device-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="side-header">
        <p className="title">{t('deviceDetails')}</p>
        <div className="action-buttons">
          <ResetIcon className="icon fill" onClick={() => handleResetAction()} />
          <QRIcon className="icon fill" onClick={() => handleQrAction()} />
          <EditIcon className="icon" onClick={toggleEditMode} />
          <DeleteIcon className="icon delete" onClick={() => handleDeleteAction()} />
        </div>
      </div>
      <div className="device-side-panel-container">
        <div className="device-preview">
          <div className="device-image-status-wrap">
            <div className="device-status-text-wrap">
              <div
                className={`device-status-text-container ${deviceDetails.isDeviceOffline ? 'offline' : 'online'}`}>
                <div className="icon" />
                <p>{deviceDetails.isDeviceOffline ? t('offline') : t('online')}</p>
              </div>
            </div>
            {deviceImage !== '' ? (
              <img src={deviceImage} alt="" className="device-details-image" />
            ) : (
              <img
                src={branding.images.emptyChargerImage}
                alt=""
                className="device-details-image"
              />
            )}
            <div
              className={`device-status-container ${deviceDetails.isDeviceOffline ? 'offline' : 'online'}`}>
              <div className="static-container" />
              <div className="animation-container" />
            </div>
          </div>
          <div className="map-address-container">
            <div className="device-side-panel-map">
              <h6>{t('deviceLocation')}</h6>
              <div className="map-container">
                {isLoaded && validPosition && (
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={validPosition}
                    zoom={15}
                    onLoad={(map) => {
                      mapReference.current = map;
                    }}>
                    <Marker
                      position={validPosition}
                      icon={{
                        url: branding.icons.stationLocator,
                        scaledSize: new window.google.maps.Size(44, 44),
                      }}
                    />
                  </GoogleMap>
                )}
              </div>
            </div>
            <div className="address-wrap">
              <h6>{t('address')}</h6>
              <p>{deviceDetails?.address}</p>
            </div>
          </div>
        </div>
        <div className="content-wrap-with-scroll">
          {deviceDetails?.connectors && deviceDetails.connectors.length ? (
            <div className="connector-wrap">
              {deviceDetails.connectors.map((connector: any, index: number) => (
                <div className="each-connector" key={connector._id}>
                  <div className="connector-type">
                    {/* To-do -> Need to configure icon based on connector type */}
                    <span>
                      <CCS2Icon />
                    </span>
                    <h6>
                      {t('connectorLabel')} {connector.connectorId}
                    </h6>
                  </div>
                  <p>Connector {connector.connectorId}</p>
                  <div className={`connector-status ${connector.status}`}>{connector.status}</div>
                  {deviceDetails?.connectors && index !== deviceDetails.connectors.length - 1 && (
                    <div className="right-border" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <h6>{t('connectorUnavailable')}</h6>
          )}
        </div>
        <div className="device-side-panel-details">
          {isEdit ? (
            <>
              <div className="edit-device-form">
                <div className="full-width-field">
                  <h4>{t('basicInfo')}</h4>
                </div>
                {/* Station Name */}
                <SelectField
                  inputLabel={t('stationName')}
                  name="chargingStation"
                  placeholder={t('selectStation')}
                  options={allStations}
                  selectedValue={editData.chargingStation}
                  valueRequired
                  onChangeValue={handleInputChange}
                />

                {/* Device Name */}
                <CustomInput
                  inputLabel={t('deviceName')}
                  type="text"
                  name="chargingPointName"
                  placeholder={t('deviceName')}
                  value={editData.chargingPointName}
                  valueRequired
                  onChangeValue={handleInputChange}
                />

                {/* Serial Number */}
                <CustomInput
                  inputLabel={t('deviceSerial')}
                  type="text"
                  name="chargingPointSn"
                  placeholder={t('deviceSerial')}
                  value={editData.chargingPointSn}
                  valueRequired
                  onChangeValue={handleInputChange}
                />

                <SelectField
                  inputLabel={t('chargePointVendor')}
                  options={chargePointVendors}
                  placeholder={t('chargePointVendor')}
                  name="chargePointVendor"
                  valueRequired
                  selectedValue={editData?.chargePointVendor}
                  onChangeValue={handleInputChange}
                  readOnly
                />
                <SelectField
                  inputLabel={t('chargePointModel')}
                  options={chargePointModels}
                  placeholder={t('chargePointModel')}
                  name="chargePointModel"
                  valueRequired
                  selectedValue={editData?.chargePointModel}
                  onChangeValue={handleInputChange}
                  readOnly
                />
              </div>

              <div className="edit-device-form">
                <div className="full-width-field">
                  <h4>{t('chargerInfo')}</h4>
                </div>
                <CustomInputSelectBox
                  inputLabel={t('maximumPowerOutput')}
                  inputType="number"
                  placeholder={t('enterValue')}
                  inputName="maximumOutputPower"
                  selectName="maximumOutputPowerUnit"
                  valueRequired
                  inputValue={editData.maximumOutputPower}
                  selectValue={editData.maximumOutputPowerUnit}
                  selectOptions={maxPowerOutputUnits}
                  handleValueChange={handleInputChange}
                />
                <CustomInputSelectBox
                  inputLabel={t('desiredPowerOutput')}
                  inputType="number"
                  placeholder={t('enterValue')}
                  inputName="desiredPowerOutput"
                  selectName="powerUnit"
                  inputValue={editData.desiredPowerOutput}
                  handleValueChange={handleInputChange}
                  valueRequired
                  selectValue={editData.powerUnit}
                  selectOptions={desiredPowerOutputUnits}
                />

                {/* Multi-select field for connectorType */}
                {editData.connectors?.map((connector: Connector) => (
                  <div key={connector.connectorId} className="full-width-field">
                    <MultiSelectDropdown
                      placeholderFilled={`${t('connectorLabel')} ${connector.connectorId} ${t('type')}`}
                      title={`${t('connectorLabel')} ${connector.connectorId} ${t('type')}`}
                      options={connectorTypes[currentBranding]}
                      name={`connectorType_${connector.connectorId}`}
                      placeholder={`${t('selectConnector')} ${connector.connectorId} ${t('type')}`}
                      search
                      selectedChips
                      selectedValues={connector.connectorType || []}
                      onSelectionChange={(_name, value) =>
                        handleConnectorTypeChange(connector.connectorId, value as string[])
                      }
                      valueRequired
                    />
                  </div>
                ))}

                {/* PnC Mode */}
                {editData.chargePointVendor === 'JuiceBox' && (
                  <div className="full-width-field">
                    <CustomInputSwitch
                      inputLabel={t('pnCMode')}
                      value={editData.isJuiceBoxPncEnabled}
                      name="isJuiceBoxPncEnabled"
                      onChangeValue={handleInputChange}
                    />
                  </div>
                )}

                {/* Tx Default Profile */}
                {editData.chargePointVendor === 'JuiceBox' && (
                  <div className="full-width-field">
                    <CustomInputSwitch
                      inputLabel={t('txDefaultProfile')}
                      value={editData.useTxDefaultProfile}
                      name="useTxDefaultProfile"
                      onChangeValue={handleInputChange}
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="sidebar-action-buttons">
                  <MyButton label={t('cancel')} onClickFunc={toggleEditMode} buttonType="outline" />
                  <MyButton
                    label={t('save')}
                    onClickFunc={handleSubmitDeviceDetails}
                    buttonType="submit"
                    loading={formStatus.buttonLoader}
                    disableButton={formStatus.buttonLoader}
                  />
                </div>
              </div>
            </>
          ) : (
            deviceGeneralDetails.map((generalDetail) => (
              <div className="each-device-data" key={generalDetail.id}>
                <h6>{generalDetail.label}</h6>
                <p
                  role="presentation"
                  onClick={generalDetail.handleClick ? generalDetail.handleClick : () => {}}
                  className={generalDetail.handleClick ? 'hyperlink' : ''}>
                  {String(deviceDetails[generalDetail.key as keyof ChargingPointProperties] || '-')}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
      <div className="device-side-panel-description" />
      <div
        className={`device-panel-station-view-wrap ${overlaySidePanel === 'station' || overlaySidePanel === 'facility' ? 'active' : ''}`}>
        <div className="side-panel-header">
          {/* Need to configure after new tab integration */}
          <span style={{ opacity: '0.5', cursor: 'not-allowed' }}>{/* <NewTabIcon /> */}</span>
          <span role="presentation" onClick={() => setOverlaySidePanel('closed')}>
            <CloseIcon />
          </span>
        </div>
        {overlaySidePanel === 'station' ? (
          <div className="side-panel-content">
            <StationSidePanel fetchIdFrom="props" id={deviceDetails.chargingStation} />
          </div>
        ) : (
          <div className="side-panel-content">
            {/* <FacilitySidePanel fetchIdFrom="props" id={deviceDetails.accountCustomId} /> */}
            <div />
          </div>
        )}
      </div>
    </div>
  );
}
